import { TextButton } from "@components/library";
import { FEATURE_FLAGS, WINDOW_DIMENSIONS } from "@constants";
import { RootState } from "@redux/store";
import {
  company_scientist_network_path,
  discover_path,
  new_opportunity_path,
  profile_page_path,
} from "@routes/routes";
import { marketplacePath } from "@routes/utils";
import { getPrevUrlCookie } from "@utils/authUtils";
import { useSelector } from "react-redux";
import { RequestType } from "src/App";
import styled from "styled-components";
import { UserInfo } from "../OnboardingContainer";
import * as S from "../StepStyles";
import Step4NextStep from "./Step4NextStep";

interface Props {
  userType: UserInfo["role"];
}

const Step4 = ({ userType }: Props) => {
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const subscription = useSelector((state: RootState) => state.company.subscription);

  const routeToPrevUrl = () => {
    getPrevUrlCookie(marketplacePath("requests", { follow: "true" }), false, currentUser.role);
  };

  const hasPreviousAction = Boolean(getPrevUrlCookie("", false, currentUser.role ?? 0, false));

  return (
    <S.StepContainer width="592px">
      <S.StepHeader
        subheading="Get discovered"
        heading={
          <>
            Welcome, {currentUser.first_name}!
            <br />
            Now that you&rsquo;re signed up, let&rsquo;s improve your visibility to potential
            partners.
          </>
        }
      />
      <NextStepsContainer>
        {(() => {
          switch (userType) {
            case "research-scientist":
            case "company-scientist":
              return (
                <>
                  <Step4NextStep
                    iconName="Profile"
                    title="Refine your profile"
                    description="Describe what you&rsquo;re working on, what you&rsquo;re looking for, and more."
                    to={profile_page_path(currentUser.profile_id)}
                    width="276px"
                  />
                  {FEATURE_FLAGS.INNOVATOR_SOLUTION_LISTINGS && (
                    <Step4NextStep
                      iconName="RFP"
                      title="Add a partnering listing"
                      description="Share your technologies, products or services for industry to discover."
                      to={profile_page_path(currentUser.profile_id, { partnering_listings: true })}
                      width="276px"
                    />
                  )}
                </>
              );
            case "company-sponsor":
              return (
                <>
                  <Step4NextStep
                    iconName="RFP"
                    title="Draft your first partnering request"
                    description="Create a draft of your request and submit it for publishing."
                    to={new_opportunity_path({ type: RequestType.REQUEST_FOR_PROPOSAL })}
                    width="280px"
                  />
                  {FEATURE_FLAGS.DISCOVER_FEED ? (
                    <Step4NextStep
                      iconName="Search"
                      title="Explore partners and new solutions"
                      description="Explore your personalized feed of solutions or search Halo's network of experts."
                      to={discover_path()}
                      width="280px"
                    />
                  ) : (
                    subscription?.features.company_scientist_network &&
                    (subscription.features.global_network_search ? (
                      <Step4NextStep
                        iconName="Search"
                        title="Search for partners on Halo's network"
                        description="Search our network of experts actively seeking to partner."
                        to={company_scientist_network_path()}
                        width="280px"
                      />
                    ) : (
                      <Step4NextStep
                        iconName="Profile"
                        title="Help build your organization's network"
                        description="Use Halo to centralize your network of existing partners for your team."
                        to={company_scientist_network_path()}
                        width="280px"
                      />
                    ))
                  )}
                </>
              );
            case "research-administrator":
              return (
                <>
                  <Step4NextStep
                    iconName="Profile"
                    title="Invite faculty members to join Halo"
                    description="Introduce researchers to Halo to help them find relevant partnering requests."
                    to="/"
                    width="280px"
                  />
                  <Step4NextStep
                    iconName="Search"
                    title="Find partnering requests on the marketplace"
                    description="Find partnering requests to share with faculty members."
                    to={marketplacePath("requests")}
                    width="280px"
                  />
                </>
              );
          }
        })()}
      </NextStepsContainer>
      {(hasPreviousAction || userType === "company-sponsor") && (
        <TextButton
          onClick={routeToPrevUrl}
          text="...or continue where you left off"
          size="md"
          iconName="Arrow Right"
          margin="48px 0 0"
        />
      )}
    </S.StepContainer>
  );
};

export default Step4;

const NextStepsContainer = styled.div`
  display: flex;
  gap: 32px;

  ${WINDOW_DIMENSIONS.TABLET_MEDIA_QUERY} {
    flex-direction: column;
  }
`;
